import type { OwnerResponse, Pageable } from '@infuse/api/common';
import { getOrganisation } from '@infuse/api/iam/organisation';
import { getTenant, getTenantOrganisation } from '@infuse/api/iam/tenant';
import { AssignmentItem, AssignRequest } from '../model/model';

/**
 * Generic composable for entity access assignment
 * Handles fetching owners, assigning and unassigning access for any entity type
 */
export function useEntityAccessAssignment<TGetOwnersPath, TAssignPath, TUnassignPath, TAssignRequest, TUnassignRequest>(
  getOwners: (
    params: { size: number; page: number },
    query: undefined,
    path: TGetOwnersPath
  ) => Promise<Pageable<OwnerResponse>>,
  assign: (path: TAssignPath, request: TAssignRequest) => Promise<void>,
  unassign: (path: TUnassignPath, request: TUnassignRequest) => Promise<void>
) {
  async function handleFetchOwners(entityId: string): Promise<AssignmentItem[]> {
    const pageable = await getOwners({ size: 1000, page: 0 }, undefined, { id: entityId } as TGetOwnersPath);

    const assignmentPromises = pageable.content.map(async (owner): Promise<AssignmentItem> => {
      // If name and type are already provided, use them directly
      if (owner.name && owner.type) {
        return {
          id: owner.rootId,
          name: owner.name,
          type: owner.type,
          parentId: owner.parentId,
          inherited: owner.inherited,
          inheritedFromType: owner.inheritedFromType,
          externallyManaged: owner.externallyManaged
        };
      }

      if (owner.parentId) {
        // Tenant-level organisation
        const org = await getTenantOrganisation({ id: owner.parentId, organisationId: owner.rootId });
        return {
          id: owner.rootId,
          name: org.name,
          type: 'organisation' as const,
          parentId: owner.parentId,
          inherited: owner.inherited,
          inheritedFromType: owner.inheritedFromType,
          externallyManaged: owner.externallyManaged
        };
      } else {
        // Could be tenant or domain-level organisation - try organisation first, then tenant
        try {
          const org = await getOrganisation({ id: owner.rootId });
          return {
            id: owner.rootId,
            name: org.name,
            type: 'organisation' as const,
            inherited: owner.inherited,
            inheritedFromType: owner.inheritedFromType,
            externallyManaged: owner.externallyManaged
          };
        } catch {
          // If not an organisation, try tenant - if this also fails, the promise will reject and be skipped
          const tenant = await getTenant({ id: owner.rootId });
          return {
            id: owner.rootId,
            name: tenant.name,
            type: 'tenant' as const,
            inherited: owner.inherited,
            inheritedFromType: owner.inheritedFromType,
            externallyManaged: owner.externallyManaged
          };
        }
      }
    });

    const results = await Promise.allSettled(assignmentPromises);

    // Extract successful assignments, skipping failed ones
    return results
      .filter((result): result is PromiseFulfilledResult<AssignmentItem> => result.status === 'fulfilled')
      .map((result) => result.value);
  }

  async function handleAssign(entityId: string, request: AssignRequest): Promise<void> {
    const assignRequest = {
      rootId: request.rootId
    } as TAssignRequest;

    if (request.parentId) {
      (assignRequest as TAssignRequest & { parentId?: string }).parentId = request.parentId;
    }

    await assign({ id: entityId } as TAssignPath, assignRequest);
  }

  async function handleUnassign(entityId: string, request: AssignRequest): Promise<void> {
    const unassignRequest = {
      rootId: request.rootId
    } as TUnassignRequest;

    if (request.parentId) {
      (unassignRequest as TUnassignRequest & { parentId?: string }).parentId = request.parentId;
    }

    await unassign({ id: entityId } as TUnassignPath, unassignRequest);
  }

  return {
    handleFetchOwners,
    handleAssign,
    handleUnassign
  };
}
